import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useHistory } from 'react-router-dom';
import t from '@guestyci/localize/t.macro';
import {
  Button,
  Input,
  Text,
  Heading,
  Stack,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  createSchema,
  email as emailValidator,
} from '@guestyci/arc';

import { utils } from 'utils';

const forgotPasswordSchema = createSchema({
  email: emailValidator(),
});

const ForgotPasswordForm = ({ onResetSent }) => {
  const history = useHistory();
  const [error, setError] = useState(null);

  const form = useForm({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
    mode: 'onSubmit',
  });

  const handleSubmit = async (data) => {
    setError(null);
    try {
      utils.eventsLogger.unifiedLoginDio.track('reset_password', 'click', {
        source: 'reset_page',
      });
      await utils.api.resetPassword(data.email);
      onResetSent();
    } catch (e) {
      setError(t('Incorrect email, please try again'));
    }
  };

  const handleBackToSignIn = () => {
    history.push('/login');
  };

  return (
    <Stack spacing={6} className="gst-pt-6 lg:gst-pt-0">
      <Stack spacing={2}>
        <Heading variant="h2">{t('Forgot password?')}</Heading>
        <Text type="secondary">
          {t("We'll email you a link you can use to reset your password securely.")}
        </Text>
      </Stack>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
          <Stack spacing={10}>
            <FormField
              control={form.control}
              name="email"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t('Email')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder={t('e.g. name@company.com')}
                      autoFocus
                      autoComplete="email"
                      disabled={form.formState.isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                  {error && !fieldState.error && (
                    <Text type="error" size="sm">
                      {error}
                    </Text>
                  )}
                </FormItem>
              )}
            />

            <Stack spacing={2} align="center">
              <Button
                type="submit"
                className="gst-w-full"
                isLoading={form.formState.isSubmitting}
                disabled={form.formState.isSubmitting}
              >
                {t('Reset password')}
              </Button>

              <Button
                type="button"
                variant="plain"
                onClick={handleBackToSignIn}
                disabled={form.formState.isSubmitting}
              >
                {t('Back to sign in')}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Form>
    </Stack>
  );
};

export default ForgotPasswordForm;
